import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, isValid } from 'date-fns';
import Navbar from '@/components/layout/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  ArrowLeft,
  Loader2,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Calendar,
  MapPin,
  Sparkles,
  Instagram,
  History,
  Settings,
} from 'lucide-react';
import ScrapeInstagramButton from '@/components/admin/ScrapeInstagramButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AMENITY_KEYS = [
  ['has_bar', 'Bar'],
  ['has_food_vendors', 'Food'],
  ['has_onsite_parking', 'Parking'],
  ['has_toilets', 'Toilets'],
  ['has_skate_hire', 'Skate hire'],
];

/** Render YYYY-MM-DD as "Sat 27 Feb 2026" — unambiguous for both US and UK readers. */
const prettyDate = (iso) => {
  if (!iso) return null;
  try {
    const d = parseISO(iso);
    if (!isValid(d)) return iso;
    return format(d, 'EEE d MMM yyyy');
  } catch {
    return iso;
  }
};

const AdminPendingEventsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [lastJob, setLastJob] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  // Exposed via ref so the bulk-approve handler can trigger a manual reload
  // without re-running the entire effect.
  const reloadRef = useRef(null);

  // Light polling while the page is open — picks up events streamed in
  // by a background scrape job without the user having to refresh. The
  // `cancelled` flag stops state-setting after unmount (keeps the strict
  // React 19 `set-state-in-effect` rule happy).
  useEffect(() => {
    let cancelled = false;
    const fetchPending = async () => {
      try {
        const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
        const [pendingRes, jobsRes] = await Promise.all([
          axios.get(`${API}/admin/instagram/pending`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API}/admin/instagram/scrape-jobs/recent?limit=1`, {
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => ({ data: { items: [] } })),
        ]);
        if (cancelled) return;
        setItems(pendingRes.data.items || []);
        setLastJob((jobsRes.data?.items || [])[0] || null);
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || 'Could not load pending events');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    reloadRef.current = fetchPending;
    fetchPending();
    const id = setInterval(fetchPending, 6000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const load = () => reloadRef.current && reloadRef.current();

  // Bulk-approve everything in the queue with the AI-extracted values as-is.
  // Tucked behind a confirm so the admin doesn't fire it by accident.
  const approveAll = async () => {
    if (items.length === 0 || bulkBusy) return;
    const msg = items.length === 1
      ? 'Approve this 1 event with the AI-extracted values as-is?'
      : `Approve all ${items.length} visible events with the AI-extracted values as-is?\n\nAny event missing a date or name will be skipped and stay in the queue for you to fix.`;
    if (!window.confirm(msg)) return;

    setBulkBusy(true);
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/admin/instagram/pending/bulk-approve`,
        { pending_ids: items.map((p) => p.pending_id) },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const approved = (data?.approved || []).length;
      const failed = (data?.failed || []).length;
      if (approved > 0 && failed === 0) {
        toast.success(`Published ${approved} event${approved === 1 ? '' : 's'}`);
      } else if (approved > 0 && failed > 0) {
        toast.warning(`Published ${approved}. ${failed} skipped — open them to see why.`);
      } else if (failed > 0) {
        toast.error(`All ${failed} skipped — they need a name and date before approval.`);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk approve failed');
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="admin-pending-events-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 pt-24 pb-12">
        <div className="flex items-center justify-between mb-6 gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="pending-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-accent" />
            Pending Events
          </h1>
          <ScrapeInstagramButton size="sm" />
        </div>

        {/* Discreet links to history & auto-scrape settings */}
        <div className="mb-4 flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/admin/scrape-settings')}
            className="text-xs text-muted-foreground hover:text-foreground transition flex items-center gap-1"
            data-testid="open-scrape-settings-link"
          >
            <Settings className="w-3.5 h-3.5" />
            Auto-scrape settings
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/scrape-history')}
            className="text-xs text-muted-foreground hover:text-foreground transition flex items-center gap-1"
            data-testid="open-history-link"
          >
            <History className="w-3.5 h-3.5" />
            Scrape history &amp; re-queue
          </button>
        </div>

        {/* Last-run summary banner — surfaces silent dedup skips so the admin
            understands "why did only 3 events appear when I fetched 20?" */}
        {lastJob && lastJob.status === 'complete' && lastJob.summary && (
          <Card className="bg-card/40 border-border/30 mb-4" data-testid="last-scrape-summary">
            <CardContent className="p-3">
              <div className="text-xs text-muted-foreground mb-1">
                Last scrape · {(lastJob.sources || []).join(', ') || lastJob.username || ''}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                <span>
                  <span className="text-emerald-400 font-semibold">{lastJob.summary.queued || 0}</span> queued
                </span>
                <span className="text-muted-foreground">
                  <span className="text-foreground">{lastJob.summary.fetched || 0}</span> fetched
                </span>
                {(lastJob.summary.skipped_already_seen || 0) > 0 && (
                  <span className="text-muted-foreground">
                    <span className="text-foreground">{lastJob.summary.skipped_already_seen}</span> already seen
                  </span>
                )}
                {(lastJob.summary.skipped_off_topic || 0) > 0 && (
                  <span className="text-muted-foreground">
                    <span className="text-foreground">{lastJob.summary.skipped_off_topic}</span> off-topic
                  </span>
                )}
                {(lastJob.summary.skipped_past || 0) > 0 && (
                  <span className="text-muted-foreground">
                    <span className="text-foreground">{lastJob.summary.skipped_past}</span> past
                  </span>
                )}
                {(lastJob.summary.skipped_unreadable || 0) > 0 && (
                  <span className="text-muted-foreground">
                    <span className="text-foreground">{lastJob.summary.skipped_unreadable}</span> unreadable
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <Card className="bg-card/60 border-border/40">
            <CardContent className="p-10 text-center space-y-3">
              <Instagram className="w-10 h-10 mx-auto text-muted-foreground" />
              <p className="font-medium">No pending events.</p>
              <p className="text-sm text-muted-foreground">
                Tap <span className="text-foreground">Scrape Instagram</span> above to fetch fresh ones.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Bulk-approve toolbar — skim then publish the whole queue */}
            <div className="mb-4 flex items-center justify-between gap-2" data-testid="bulk-approve-toolbar">
              <p className="text-xs text-muted-foreground">
                {items.length} pending event{items.length === 1 ? '' : 's'}
              </p>
              <Button
                size="sm"
                onClick={approveAll}
                disabled={bulkBusy}
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid="bulk-approve-btn"
              >
                {bulkBusy ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                )}
                Approve all visible
              </Button>
            </div>
            <div className="space-y-4" data-testid="pending-events-list">
              {items.map((p) => (
                <PendingCard key={p.pending_id} pending={p} onChange={load} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const PendingCard = ({ pending, onChange }) => {
  const ex = pending.extracted || {};
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);

  const v = (key, fallback = '') => (edits[key] ?? ex[key] ?? fallback);
  const setV = (key, val) => setEdits((p) => ({ ...p, [key]: val }));

  const approve = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      await axios.post(
        `${API}/admin/instagram/pending/${pending.pending_id}/approve`,
        edits,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Published');
      // Bring the next pending event into view — admins are often deep into
      // the form when they hit Approve, and the queue silently re-sorts so
      // a hard scroll-to-top is the cleanest way to surface "what's next".
      window.scrollTo({ top: 0, behavior: 'smooth' });
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not publish');
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!window.confirm('Reject this event? It will be removed from the queue.')) return;
    setBusy(true);
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      await axios.delete(`${API}/admin/instagram/pending/${pending.pending_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Rejected');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not reject');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="bg-card border-border/40 overflow-hidden" data-testid={`pending-card-${pending.pending_id}`}>
      {pending.flyer_url && (
        <img
          src={pending.flyer_url}
          alt={ex.name || 'Flyer'}
          className="w-full max-h-72 object-contain bg-black/40"
        />
      )}
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base truncate">{ex.name || 'Untitled event'}</CardTitle>
          {pending.post_url && /^https?:\/\/(www\.|m\.)?instagram\.com\//.test(pending.post_url) ? (
            <a
              href={pending.post_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 flex-shrink-0"
              data-testid={`view-on-ig-${pending.pending_id}`}
            >
              <ExternalLink className="w-3 h-3" />
              View on IG
            </a>
          ) : (
            <span className="text-xs text-muted-foreground/60 flex items-center gap-1 flex-shrink-0" title="Original IG post URL missing">
              <ExternalLink className="w-3 h-3" />
              No IG link
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="text-xs">
            <Instagram className="w-3 h-3 mr-1" />
            @{pending.ig_username}
          </Badge>
          {ex.date && (
            <Badge variant="outline" className="text-xs">
              <Calendar className="w-3 h-3 mr-1" />
              {prettyDate(ex.date)} {ex.time || ''}
            </Badge>
          )}
          {ex.sport && (
            <Badge variant="outline" className="text-xs capitalize">
              {ex.sport.replace(/_/g, ' ')}
            </Badge>
          )}
          {ex.country && (
            <Badge
              variant="outline"
              className={`text-xs ${
                ex.country_source === 'address'
                  ? 'border-emerald-500/60 text-emerald-400 bg-emerald-500/10'
                  : ex.country_source === 'default_gb'
                  ? 'border-red-500/60 text-red-500 bg-red-500/10'
                  : ex.country_source === 'owner_history'
                  ? 'border-amber-500/60 text-amber-500 bg-amber-500/10'
                  : ex.country_source === 'caption_flag' || ex.country_source === 'owner_bio_flag'
                  ? 'border-fuchsia-500/60 text-fuchsia-400 bg-fuchsia-500/10'
                  : ex.country_source === 'apify_location' || ex.country_source === 'owner_bio_text'
                  ? 'border-blue-500/60 text-blue-400 bg-blue-500/10'
                  : ex.country_source === 'gemini_flyer'
                  ? 'border-purple-500/60 text-purple-400 bg-purple-500/10'
                  : ''
              }`}
              title={
                ex.country_source === 'default_gb'
                  ? '⚠️ Defaulted to GB — no location signals found. Manually verify!'
                  : ex.country_source === 'owner_history'
                  ? 'Inherited from a previous event by the same organiser'
                  : ex.country_source === 'caption_flag'
                  ? 'Detected from flag emoji in caption'
                  : ex.country_source === 'owner_bio_flag'
                  ? 'Detected from flag emoji in organiser bio'
                  : ex.country_source === 'owner_bio_text'
                  ? 'Resolved from organiser bio text via Nominatim'
                  : ex.country_source === 'apify_location'
                  ? 'From Instagram tagged location'
                  : ex.country_source === 'gemini_flyer'
                  ? 'Extracted directly from flyer text'
                  : ex.country_source === 'address'
                  ? 'Confirmed via address geocoding'
                  : `Source: ${ex.country_source || 'unknown'}`
              }
              data-testid={`country-source-${pending.pending_id}`}
            >
              {ex.country}
              {ex.country_source === 'default_gb' && ' ⚠'}
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-3 pt-2">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Name</Label>
            <Input value={v('name')} onChange={(e) => setV('name', e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex h-10 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-accent/40 transition text-left"
                  data-testid={`date-picker-${pending.pending_id}`}
                >
                  <Calendar className="w-3.5 h-3.5 mr-2 text-muted-foreground" />
                  {v('date') ? prettyDate(v('date')) : <span className="text-muted-foreground">Pick a date</span>}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarPicker
                  mode="single"
                  selected={v('date') ? parseISO(v('date')) : undefined}
                  onSelect={(d) => {
                    if (d && isValid(d)) setV('date', format(d, 'yyyy-MM-dd'));
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <div>
          <Label className="text-xs">
            <MapPin className="w-3 h-3 inline mr-1" />
            Address
          </Label>
          <Input value={v('address')} onChange={(e) => setV('address', e.target.value)} />
        </div>

        <div>
          <Label className="text-xs">Description</Label>
          <Textarea
            rows={2}
            value={v('description')}
            onChange={(e) => setV('description', e.target.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Start</Label>
            <Input
              type="time"
              value={v('time', '19:00')}
              onChange={(e) => setV('time', e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs">End</Label>
            <Input
              type="time"
              value={v('end_time', '23:00')}
              onChange={(e) => setV('end_time', e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Age / Dress code</Label>
            <Input
              placeholder="18+, all white"
              value={`${v('age_restriction') || ''}${v('dress_code') ? ' · ' + v('dress_code') : ''}`}
              onChange={(e) => {
                const [age, ...dress] = e.target.value.split('·');
                setV('age_restriction', age.trim());
                setV('dress_code', dress.join('·').trim());
              }}
            />
          </div>
          <div>
            <Label className="text-xs">External ticket link</Label>
            <Input
              placeholder="https://…"
              value={v('ticket_link')}
              onChange={(e) => setV('ticket_link', e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          {AMENITY_KEYS.map(([key, label]) => (
            <label
              key={key}
              className="flex items-center gap-2 text-xs rounded-md border border-border/40 px-2 py-1.5"
            >
              <Switch
                checked={!!v(key)}
                onCheckedChange={(val) => setV(key, !!val)}
              />
              {label}
            </label>
          ))}
        </div>

        {pending.extractor_notes && (
          <p className="text-xs text-amber-500/90">
            <Sparkles className="w-3 h-3 inline mr-1" />
            AI note: {pending.extractor_notes}
          </p>
        )}
      </CardContent>

      <div className="flex border-t border-border/40">
        <Button
          variant="ghost"
          className="flex-1 rounded-none text-red-400 hover:text-red-300"
          onClick={reject}
          disabled={busy}
          data-testid={`reject-${pending.pending_id}`}
        >
          <XCircle className="w-4 h-4 mr-1" />
          Reject
        </Button>
        <Button
          className="flex-1 rounded-none"
          onClick={approve}
          disabled={busy}
          data-testid={`approve-${pending.pending_id}`}
        >
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
          Approve &amp; publish
        </Button>
      </div>
    </Card>
  );
};

export default AdminPendingEventsPage;
